import kebabCase from 'lodash/kebabCase';
import styled from 'styled-components';
import Label from '../label';
import { autocompleteInputId } from '@/components/fulfillmentManager/deliveryManager/deliveryForm/autocomplete-input-id';
import { COLORS } from '@/constants/styles';
import { getFontStyles } from '@/redux/cmsConfig/utils';

interface Props {
  icon?: string;
  placeholder?: string;
  meta?: {
    submitFailed: boolean;
    error: string | null;
  };
  input?: { name: string };
}

export const SearchInput = ({
  placeholder,
  icon,
  meta = { submitFailed: false, error: null },
  input = { name: 'search-input' },
}: Props) => (
  <StyledInputContainer>
    {icon && <StyledIcon $icon={icon} />}
    <Label
      // TODO - eventually this could be configurable
      visuallyHidden={true}
      htmlFor={input?.name}
    >
      {placeholder}
    </Label>
    <StyledSearchInput
      {...input}
      id={input?.name}
      placeholder={placeholder}
      $error={Boolean(meta.submitFailed && meta.error)}
      aria-describedby={`${kebabCase(placeholder)}-search-input`}
    />

    <p role="alert" id={`${kebabCase(placeholder)}-search-input`}>
      {meta.submitFailed && meta.error}
    </p>
  </StyledInputContainer>
);

const StyledInputContainer = styled.div({
  paddingBottom: '10px',
  position: 'relative',

  p: {
    color: COLORS.ERROR_RED,
    fontSize: '.75em',
    marginTop: '.25rem',
  },
});

export const StyledSearchInput = styled.input<{
  $error?: boolean;
  $padding?: string;
  id?: string;
}>(({ theme, $error, $padding, id }) => ({
  backgroundColor: theme.location_search.background_color ?? 'transparent',
  border: `1px solid ${
    $error
      ? theme.location_search.error_color ?? COLORS.ERROR_RED
      : theme.location_search.border_color ?? COLORS.LIGHTERGRAY
  }`,
  borderRadius: `${theme.location_search.border_radius ?? 0}px`,
  display: 'block',
  flexBasis: '66.66%',
  height: '40px',
  width: '100%',
  textOverflow: 'ellipsis',
  padding: $padding ?? '2px 30px 0 30px',
  ...getFontStyles(theme.forms.font, ['font_family', 'font_size', 'color']),
  ...(id?.includes(autocompleteInputId) && {
    height: `${theme.forms.field_height}px`,
    marginBottom: '20px',
  }),
  '@media (max-width: 767px)': {
    // The 16px floor must apply to EVERY instance, including the delivery-address
    // autocomplete. iOS auto-zooms on focus below 16px, and inside the Capacitor
    // shell that zoom cannot be undone — Capacitor's scroll-view delegate disables
    // the pinch gesture recogniser on the first zoom and never restores it, so the
    // diner is stranded mid-checkout (TEAM-14851). The autocomplete was swept into
    // the exclusion below when it was written for the border cosmetics, which is
    // why it alone rendered at the brand's sub-16px size.
    //
    // The border overrides stay excluded: they exist only because the location
    // search box is visually welded to its submit button, and the autocomplete has
    // no such button. Keep the brand's size whenever it already clears 16px.
    ...(id?.includes(autocompleteInputId)
      ? { fontSize: `${Math.max(Number(theme.forms.font.font_size) || 15, 16)}px` }
      : {
          // Unchanged: the locations and menu search inputs keep the flat 16px
          // they have always had. Raising them to the brand size is out of scope
          // for TEAM-14851 and would reflow a fixed-height box that ellipsizes.
          fontSize: '16px',
          borderRight: 0,
          borderRadius: `${theme.location_search.border_radius ?? 0}px 0 0 ${
            theme.location_search.border_radius ?? 0
          }px}`,
        }),
  },
}));

interface StyledIconProps {
  $icon: string;
}

const StyledIcon = styled.div<StyledIconProps>`
  background-image: url(${({ $icon }) => $icon});
  background-position: 10px center;
  background-repeat: no-repeat;
  position: absolute;
  top: 0;
  left: 2px;
  height: 40px;
  width: 30px;
`;
